import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import {
  LibPipeModule,
  TableColumn,
  UtcToLocalPipe,
} from '@mynexus/mynexus-ui-lib';
import { finalize } from 'rxjs/operators';
import { FaxHistorySearchFormComponent } from '../fax-history-search-form/fax-history-search-form.component';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { FaxHistoryExtension, FaxHistorySearch } from '@app/shared/models';
import { DirectiveModule } from '@app/shared/directive/directive.module';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

import { FaxNumberPipe } from '@app/shared/pipe/fax-number.pipe';
import { FaxHistoryService } from '../services/fax-history.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    DirectiveModule,
    FaxHistorySearchFormComponent,
    FaxNumberPipe,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    LibPipeModule,
    TableModule,
    TooltipModule
  ],
  providers: [FaxHistoryService],
  selector: 'app-fax-history',
  styleUrls: ['./fax-history-panel.component.scss'],
  templateUrl: './fax-history-panel.component.html'
})

export class FaxHistoryPanelComponent {
  faxHistorySearchResultsList: Array<FaxHistoryExtension> = [];
  isLoading = false;
  searchParams: FaxHistorySearch = null;
  firstPage = 0;
  columns = [
    new TableColumn('Auth #', 'authNumber', '10rem'),
    new TableColumn('Created By', 'createdBy', '16rem'),
    new TableColumn('Provider Location', 'providerLocation', '12rem'),
    new TableColumn('Fax #', 'recipientFaxNumber', '9rem'),
    new TableColumn('Subject', 'subject', '9rem'),
    new TableColumn('Recipient Name', 'recipientName', '11rem'),
    new TableColumn('Status', 'status', '7rem'),
    new TableColumn('Comm. Type', 'communicationType', '10rem'),
    new TableColumn('Date In Queue', 'dateInQueue', '13rem'),
    new TableColumn('Fail Reason', 'failReason', '10rem'),
  ] as TableColumn[];

  readonly filterableFields: string[] = [
    'authNumber',
    'createdBy',
    'providerLocation',
    'recipientFaxNumber',
    'subject',
    'recipientName',
    'status',
    'communicationType',
    'failReason',
    'dateInQueue',
  ];

  filterTableDateConfig = [
    {
      field: 'dateInQueue',
      pipe: UtcToLocalPipe,
      transform: 'MM/dd/yyyy hh:mm:ss a',
    },
  ];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly faxHistoryService: FaxHistoryService
  ) {}

  resendFax(faxId: string) {
    this.faxHistoryService
      .resendFax(faxId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.submitFaxHistorySearch(this.searchParams);
        this.ref.markForCheck();
      });
  }

  submitFaxHistorySearch(params: FaxHistorySearch) {
    this.searchParams = params;
    this.faxHistorySearchResultsList = [];
    this.isLoading = true;
    const searchParams = {
      productId: params.productId,
      startDate: params.startDate,
      endDate: params.endDate,
      users: params.selectedUser,
      statuses: params.status ? [params.status] : null,
      lineOfBusiness: params.lineOfBusiness ? params.lineOfBusiness : null,
    };

    this.faxHistoryService
      .getFaxHistoryListByFilters(searchParams)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.ref.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: FaxHistoryExtension[]) => {
        const mappedResult = result.map(fax=> ({ ...fax, createdBy: this.manageCreatedBy(fax.createdBy)}));
        this.firstPage = 0;
        this.faxHistorySearchResultsList = mappedResult;
      });
  }

  manageCreatedBy(createdBy: string) {
    return createdBy === 'Automated Letter Creation' || createdBy === 'mynadmin'
      ? 'Portal'
      : createdBy;
  }
}
